import { ReactNode, lazy, Suspense, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Header } from "./Header";
import { Footer } from "./Footer";
import PageSections from "@/components/PageSections";

const ChatWidget = lazy(() => import("@/components/ChatWidget").then(m => ({ default: m.ChatWidget })));

// CookieConsentProvider now lives at the app root, above VisitorTrackingProvider,
// so visitor tracking can react to consent being granted.

import { CookieConsent } from "@/components/CookieConsent";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { LinkedInInsightTag } from "@/components/LinkedInInsightTag";
import { PageTransition } from "@/components/animations/PageTransition";
import { PersonaBanner } from "@/components/PersonaBanner";
import { AutoBreadcrumb, BreadcrumbProvider } from "@/components/layout/AutoBreadcrumb";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [chatReady, setChatReady] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = (window as any).requestIdleCallback(() => setChatReady(true), { timeout: 4000 });
      return () => (window as any).cancelIdleCallback(id);
    }
    const timer = setTimeout(() => setChatReady(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // A CTA clicked before Tanya has mounted asks for her straight away
  // (same listener as LandingLayout), instead of waiting up to 6 s for
  // the idle mount and then falling back to /contact.
  useEffect(() => {
    const onRequest = () => setChatReady(true);
    window.addEventListener("tanya:mount-request", onRequest);
    return () => window.removeEventListener("tanya:mount-request", onRequest);
  }, []);

  // Admin "Try Tanya" deep-link: when the live site is opened with
  // ?tanya=open, fire the open event a few times after the chat widget has
  // mounted so the lazy listener is guaranteed to catch it.
  useEffect(() => {
    if (!chatReady || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("tanya") !== "open") return;
    const delays = [400, 900, 1600, 2600];
    const timers = delays.map((d) =>
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("tanya:open", { detail: { mode: "admin_test" } }),
        );
      }, d),
    );
    return () => { timers.forEach(clearTimeout); };
  }, [chatReady]);

  return (
    <>
      <BreadcrumbProvider>

      <div className="flex min-h-screen flex-col">
        <div className="sticky top-0 z-50">
          <Header />
          <PersonaBanner />
          <AutoBreadcrumb />
        </div>
        <AnimatePresence mode="wait">
          <PageTransition key={location.pathname}>
            <main className="flex-1">
              <PageSections position="top" />
              {children}
              <PageSections position="bottom" />
            </main>
          </PageTransition>
        </AnimatePresence>
        <Footer />
        {chatReady && <Suspense fallback={null}><ChatWidget /></Suspense>}
        
        <CookieConsent />
        <GoogleAnalytics />
        <LinkedInInsightTag />
      </div>
      </BreadcrumbProvider>
    </>

  );
}
